import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBrands } from "store/slices/brandSlice";

/**
 * The shared brand list, fetched on demand.
 *
 * Both brand menus — `BrandsDropdown` on the desktop nav and the Brands section of
 * `MobileDrawer` — show all 313 brands, and both are surfaces most page views never open.
 * So neither fetches on mount: they call `loadBrands()` the first time the menu is opened,
 * and `loadBrands` no-ops once the list is in the store or a request is already in flight.
 *
 * Whichever menu opens first pays for the fetch and the other reads the same slice, so
 * switching between mobile and desktop widths costs one request, not two.
 *
 * @returns {{ brands: Array, loading: boolean, error: unknown, loadBrands: Function }}
 */
export default function useBrandList() {
  const dispatch = useDispatch();
  const {
    brands = [],
    loading = false,
    error = null,
  } = useSelector((state) => state.brand || {});

  const loadBrands = useCallback(() => {
    if (!brands.length && !loading) dispatch(fetchBrands());
  }, [brands.length, dispatch, loading]);

  return { brands, loading, error, loadBrands };
}
